import React from 'react';
import {
    Sparkles, RefreshCw, TrendingUp, TrendingDown, Pause, Scale,
    Award, AlertTriangle, CheckCircle, DollarSign, Target, Lightbulb
} from 'lucide-react';
import { formatCurrency, formatPercent } from '../utils/helpers';

const getScoreColor = (score) => {
    if (score >= 7) return 'var(--color-success)';
    if (score >= 5) return 'var(--color-warning)';
    return 'var(--color-error)';
};

const getGrade = (score) => {
    if (score >= 9) return 'A+';
    if (score >= 8) return 'A';
    if (score >= 7) return 'B+';
    if (score >= 6) return 'B';
    if (score >= 5) return 'C';
    return 'D';
};

const getPriorityColor = (priority) => {
    switch (priority) {
        case 'high': return 'var(--color-error)';
        case 'medium': return 'var(--color-warning)';
        case 'low': return 'var(--color-success)';
        default: return 'var(--color-text-secondary)';
    }
};

const getActionIcon = (action) => {
    switch (action) {
        case 'pause': return <Pause size={16} />;
        case 'scale': return <TrendingUp size={16} />;
        case 'test': return <Sparkles size={16} />;
        case 'refresh': return <RefreshCw size={16} />;
        default: return <AlertTriangle size={16} />;
    }
};

const getActionColor = (action) => {
    switch (action) {
        case 'pause': return 'var(--color-error)';
        case 'scale': return 'var(--color-success)';
        case 'test': return 'var(--color-primary)';
        default: return 'var(--color-warning)';
    }
};

const CampaignAIAnalysis = ({
    campaign,
    creatives,
    analysis,
    isAnalyzing,
    onAnalyze
}) => {
    const hasAnalysis = !!analysis;

    return (
        <div style={{
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border-light)',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--space-4)',
                borderBottom: hasAnalysis ? '1px solid var(--color-border-light)' : 'none'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <Sparkles size={20} style={{ color: 'var(--color-primary)' }} />
                    <h4 style={{ margin: 0 }}>Campaign AI Analysis</h4>
                    {hasAnalysis && (
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            padding: 'var(--space-1) var(--space-2)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            background: getScoreColor(analysis.campaignScore),
                            color: 'white'
                        }}>
                            {getGrade(analysis.campaignScore)} ({analysis.campaignScore?.toFixed(1)})
                        </span>
                    )}
                </div>
                <button
                    onClick={onAnalyze}
                    disabled={isAnalyzing || creatives.length === 0}
                    className={`btn ${hasAnalysis ? 'btn-secondary' : 'btn-primary'}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
                >
                    {isAnalyzing ? (
                        <>
                            <RefreshCw size={16} className="spin" /> Analyzing...
                        </>
                    ) : (
                        <>
                            <Sparkles size={16} /> {hasAnalysis ? 'Re-analyze Campaign' : 'Analyze Campaign'}
                        </>
                    )}
                </button>
            </div>

            {/* No Analysis State */}
            {!hasAnalysis && !isAnalyzing && (
                <div style={{
                    padding: 'var(--space-6)',
                    textAlign: 'center',
                    color: 'var(--color-text-secondary)'
                }}>
                    <Lightbulb size={32} style={{ opacity: 0.3, marginBottom: 'var(--space-2)' }} />
                    <p>Click "Analyze Campaign" to get AI-powered recommendations</p>
                    <p style={{ fontSize: 'var(--font-size-sm)', opacity: 0.7 }}>
                        Compare {creatives.length} creatives and identify top performers
                    </p>
                </div>
            )}

            {/* Analyzing State */}
            {isAnalyzing && (
                <div style={{
                    padding: 'var(--space-8)',
                    textAlign: 'center'
                }}>
                    {/* Animated sparkles icon */}
                    <div style={{
                        position: 'relative',
                        display: 'inline-block',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <Sparkles
                            size={48}
                            style={{
                                color: 'var(--color-primary)',
                                animation: 'pulse 1.5s ease-in-out infinite'
                            }}
                        />
                    </div>
                    <h4 style={{
                        marginBottom: 'var(--space-2)',
                        color: 'var(--color-text-primary)'
                    }}>
                        Analyzing Campaign
                    </h4>
                    <p style={{
                        color: 'var(--color-text-secondary)',
                        marginBottom: 'var(--space-4)'
                    }}>
                        Comparing {creatives.length} creatives and identifying patterns...
                    </p>
                    {/* Progress bar animation */}
                    <div style={{
                        width: '200px',
                        height: '4px',
                        background: 'var(--color-bg-tertiary)',
                        borderRadius: 'var(--radius-full)',
                        margin: '0 auto',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: '40%',
                            height: '100%',
                            background: 'var(--color-primary)',
                            borderRadius: 'var(--radius-full)',
                            animation: 'slideProgress 1.5s ease-in-out infinite'
                        }} />
                    </div>
                    <style>{`
                        @keyframes pulse {
                            0%, 100% { opacity: 1; transform: scale(1); }
                            50% { opacity: 0.7; transform: scale(1.1); }
                        }
                        @keyframes slideProgress {
                            0% { transform: translateX(-100%); }
                            100% { transform: translateX(350%); }
                        }
                    `}</style>
                </div>
            )}

            {/* Analysis Results */}
            {hasAnalysis && !isAnalyzing && (
                <div style={{ padding: 'var(--space-4)' }}>
                    {/* Summary */}
                    {analysis.summary && (
                        <p style={{
                            marginBottom: 'var(--space-4)',
                            padding: 'var(--space-3)',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            {analysis.summary}
                        </p>
                    )}

                    {/* Aggregate Metrics */}
                    {analysis.aggregateMetrics && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: 'var(--space-3)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <div style={{
                                padding: 'var(--space-3)',
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-md)',
                                textAlign: 'center'
                            }}>
                                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Total Spend</div>
                                <div style={{ fontWeight: 700 }}>{formatCurrency(analysis.aggregateMetrics.totalSpend)}</div>
                            </div>
                            <div style={{
                                padding: 'var(--space-3)',
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-md)',
                                textAlign: 'center'
                            }}>
                                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Total Revenue</div>
                                <div style={{ fontWeight: 700 }}>{formatCurrency(analysis.aggregateMetrics.totalRevenue)}</div>
                            </div>
                            <div style={{
                                padding: 'var(--space-3)',
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-md)',
                                textAlign: 'center'
                            }}>
                                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Avg ROAS</div>
                                <div style={{
                                    fontWeight: 700,
                                    color: analysis.aggregateMetrics.avgRoas >= 2 ? 'var(--color-success)' :
                                        analysis.aggregateMetrics.avgRoas >= 1 ? 'var(--color-warning)' : 'var(--color-error)'
                                }}>
                                    {analysis.aggregateMetrics.avgRoas.toFixed(2)}x
                                </div>
                            </div>
                            <div style={{
                                padding: 'var(--space-3)',
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-md)',
                                textAlign: 'center'
                            }}>
                                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Avg CTR</div>
                                <div style={{ fontWeight: 700 }}>{analysis.aggregateMetrics.avgCtr.toFixed(2)}%</div>
                            </div>
                        </div>
                    )}

                    {/* Top Performer */}
                    {analysis.topPerformer && (
                        <div style={{
                            padding: 'var(--space-4)',
                            background: 'var(--color-success-bg)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-success)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginBottom: 'var(--space-2)',
                                color: 'var(--color-success)'
                            }}>
                                <Award size={18} />
                                <span style={{ fontWeight: 600 }}>Top Performer</span>
                            </div>
                            <p style={{ fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                                {analysis.topPerformer.creativeName}
                            </p>
                            {/* Show actual metrics if available */}
                            {analysis.topPerformer.metrics && (
                                <div style={{
                                    display: 'flex',
                                    gap: 'var(--space-4)',
                                    marginBottom: 'var(--space-2)',
                                    fontSize: 'var(--font-size-sm)'
                                }}>
                                    <span>
                                        <strong>ROAS:</strong>{' '}
                                        <span style={{ color: 'var(--color-success)' }}>
                                            {(analysis.topPerformer.metrics.roas || 0).toFixed(2)}x
                                        </span>
                                    </span>
                                    <span><strong>Spend:</strong> {formatCurrency(analysis.topPerformer.metrics.spend || 0)}</span>
                                    <span><strong>CTR:</strong> {(analysis.topPerformer.metrics.ctr || 0).toFixed(2)}%</span>
                                </div>
                            )}
                            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                {analysis.topPerformer.reason}
                            </p>
                        </div>
                    )}

                    {/* Recommendations */}
                    {analysis.recommendations && analysis.recommendations.length > 0 && (
                        <div style={{ marginBottom: 'var(--space-4)' }}>
                            <h5 style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 600,
                                marginBottom: 'var(--space-3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)'
                            }}>
                                <Target size={16} /> Recommendations
                            </h5>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                {analysis.recommendations.map((rec, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            padding: 'var(--space-3)',
                                            borderRadius: 'var(--radius-md)',
                                            background: 'var(--color-bg-secondary)',
                                            borderLeft: `4px solid ${getActionColor(rec.action)}`
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            marginBottom: 'var(--space-2)'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                <span style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-1)',
                                                    padding: 'var(--space-1) var(--space-2)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    fontSize: 'var(--font-size-xs)',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    background: getActionColor(rec.action),
                                                    color: 'white'
                                                }}>
                                                    {getActionIcon(rec.action)}
                                                    {rec.action}
                                                </span>
                                                <span style={{
                                                    padding: '2px 6px',
                                                    borderRadius: 'var(--radius-sm)',
                                                    fontSize: 'var(--font-size-xs)',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    background: getPriorityColor(rec.priority),
                                                    color: 'white'
                                                }}>
                                                    {rec.priority}
                                                </span>
                                            </div>
                                        </div>
                                        <p style={{ fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                                            {rec.creativeName}
                                        </p>
                                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-1)' }}>
                                            {rec.reason}
                                        </p>
                                        {rec.expectedImpact && (
                                            <p style={{
                                                fontSize: 'var(--font-size-sm)',
                                                color: rec.action === 'pause' ? 'var(--color-error)' : 'var(--color-success)',
                                                fontWeight: 500
                                            }}>
                                                Expected Impact: {rec.expectedImpact}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Budget Recommendation */}
                    {analysis.budgetRecommendation && (
                        <div style={{
                            padding: 'var(--space-3)',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginBottom: 'var(--space-2)',
                                color: 'var(--color-primary)'
                            }}>
                                <DollarSign size={16} />
                                <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Budget Recommendation</span>
                            </div>
                            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                {analysis.budgetRecommendation}
                            </p>
                        </div>
                    )}

                    {/* New Creative Suggestion */}
                    {analysis.newCreativeSuggestion && (
                        <div style={{
                            padding: 'var(--space-3)',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-md)'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginBottom: 'var(--space-2)',
                                color: 'var(--color-primary)'
                            }}>
                                <Lightbulb size={16} />
                                <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Next Creative to Test</span>
                            </div>
                            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                {analysis.newCreativeSuggestion}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CampaignAIAnalysis;
